import { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { getApiPrefix } from '../utils/apiHelper';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DateInput } from "../components/ui/date-input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { Plus, Trash2, Loader2, Home, Pencil, Calendar, Euro, RefreshCw } from "lucide-react";
import { usePermissions } from "../hooks/usePermissions";
import { useFormValidation } from "../hooks/useFormValidation";
import { InputWithError } from "../components/common/InputWithError";
import DataTable from "../components/ui/data-table";
import { useTranslation } from "../contexts/I18nContext";

export default function AppartamentiPage() {
  const { t } = useTranslation();
  const { canEdit } = usePermissions();
  const canEditAppartamenti = canEdit("appartamenti");
  
  const [appartamenti, setAppartamenti] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [savingAppartamento, setSavingAppartamento] = useState(false);
  const [deletingAppartamento, setDeletingAppartamento] = useState(null);
  
  // Periodi contrattuali
  const [periodi, setPeriodi] = useState([]);
  const [loadingPeriodi, setLoadingPeriodi] = useState(false);
  const [periodoForm, setPeriodoForm] = useState({ data_inizio: "", data_fine: "", canone_mensile: "", importo_spese_condominiali: "", note: "" });
  const [editingPeriodoId, setEditingPeriodoId] = useState(null);
  
  // Filtro stato
  const [filtroStato, setFiltroStato] = useState("attivo");
  const [form, setForm] = useState({
    indirizzo: "",
    citta: "",
    area: "",
    canone_mensile: "",
    proprietario: "",
    iban_proprietario: "",
    frequenza_pagamento: "mensile",
    posti_letto: "1",
    data_inizio_contratto: "",
    data_fine_contratto: "",
    giorno_pagamento: "1",
    note: "",
    // Conto per canone
    conto_canone_id: "",
    // Spese condominiali
    ha_spese_condominiali: false,
    importo_spese_condominiali: "",
    conto_spese_condominiali_id: "",
    // Dettaglio copertura spese condominiali
    spese_cond_spese_generali: false,
    spese_cond_luce: false,
    spese_cond_gas: false,
    spese_cond_acqua: false,
    spese_cond_tari: false,
    // Agenzia immobiliare
    tramite_agenzia: false,
    nome_agenzia: ""
  });

  // Validazione form appartamento
  const { errors: formErrors, validate: validateForm, validateField, clearErrors: clearFormErrors } = useFormValidation({
    indirizzo: { required: true },
    citta: { required: true },
    canone_mensile: { required: true, number: true, min: 0 }
  });

  const fetchAppartamenti = useCallback(async () => {
    try {
      const prefix = getApiPrefix();
      const [appRes, accRes] = await Promise.all([
        api.get(`${prefix}/appartamenti`),
        api.get(`${prefix}/accounts`)
      ]);
      setAppartamenti(appRes.data);
      setAccounts(accRes.data);
    } catch (err) {
      handleApiError(err, t("errors.loadApartments"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAppartamenti();
  }, [fetchAppartamenti]);

  const formatCurrency = (val) =>
    new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(val || 0);

  const openAddDialog = () => {
    setEditingId(null);
    setForm({
      indirizzo: "",
      citta: "",
      area: "",
      canone_mensile: "",
      proprietario: "",
      iban_proprietario: "",
      frequenza_pagamento: "mensile",
      posti_letto: "1",
      data_inizio_contratto: "",
      data_fine_contratto: "",
      giorno_pagamento: "1",
      note: "",
      conto_canone_id: "",
      ha_spese_condominiali: false,
      importo_spese_condominiali: "",
      conto_spese_condominiali_id: "",
      spese_cond_spese_generali: false,
      spese_cond_luce: false,
      spese_cond_gas: false,
      spese_cond_acqua: false,
      spese_cond_tari: false,
      tramite_agenzia: false,
      nome_agenzia: ""
    });
    setPeriodi([]);
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingId(item.id);
    setForm({
      indirizzo: item.indirizzo || "",
      citta: item.citta || "",
      area: item.area || "",
      canone_mensile: String(item.canone_mensile || ""),
      proprietario: item.proprietario || "",
      iban_proprietario: item.iban_proprietario || "",
      frequenza_pagamento: item.frequenza_pagamento || "mensile",
      posti_letto: String(item.posti_letto || "1"),
      data_inizio_contratto: item.data_inizio_contratto || "",
      data_fine_contratto: item.data_fine_contratto || "",
      giorno_pagamento: String(item.giorno_pagamento || "1"),
      note: item.note || "",
      conto_canone_id: item.conto_canone_id || "",
      ha_spese_condominiali: item.ha_spese_condominiali || false,
      importo_spese_condominiali: String(item.importo_spese_condominiali || ""),
      conto_spese_condominiali_id: item.conto_spese_condominiali_id || "",
      spese_cond_spese_generali: item.spese_cond_spese_generali || false,
      spese_cond_luce: item.spese_cond_luce || false,
      spese_cond_gas: item.spese_cond_gas || false,
      spese_cond_acqua: item.spese_cond_acqua || false,
      spese_cond_tari: item.spese_cond_tari || false,
      tramite_agenzia: item.tramite_agenzia || false,
      nome_agenzia: item.nome_agenzia || ""
    });
    setDialogOpen(true);
    fetchPeriodi(item.id);
  };

  const handleSubmit = async () => {
    // Validazione client-side
    if (!validateForm(form)) {
      return;
    }
    setSavingAppartamento(true);
    try {
      const payload = {
        nome: form.indirizzo,
        indirizzo: form.indirizzo,
        citta: form.citta,
        area: form.area || null,
        canone_mensile: parseFloat(form.canone_mensile) || 0,
        inquilino_nome: form.proprietario || null,
        proprietario: form.proprietario || null,
        iban_proprietario: form.iban_proprietario || null,
        frequenza_pagamento: form.frequenza_pagamento || "mensile",
        posti_letto: parseInt(form.posti_letto) || 1,
        data_inizio_contratto: form.data_inizio_contratto || null,
        data_fine_contratto: form.data_fine_contratto || null,
        giorno_scadenza: parseInt(form.giorno_pagamento) || 1,
        note: form.note || null,
        // Conto canone
        conto_canone_id: form.conto_canone_id || null,
        // Spese condominiali
        ha_spese_condominiali: form.ha_spese_condominiali,
        importo_spese_condominiali: form.ha_spese_condominiali ? (parseFloat(form.importo_spese_condominiali) || 0) : 0,
        conto_spese_condominiali_id: form.ha_spese_condominiali ? (form.conto_spese_condominiali_id || null) : null,
        // Copertura spese
        spese_cond_spese_generali: form.ha_spese_condominiali && form.spese_cond_spese_generali,
        spese_cond_luce: form.ha_spese_condominiali && form.spese_cond_luce,
        spese_cond_gas: form.ha_spese_condominiali && form.spese_cond_gas,
        spese_cond_acqua: form.ha_spese_condominiali && form.spese_cond_acqua,
        spese_cond_tari: form.ha_spese_condominiali && form.spese_cond_tari,
        // Agenzia immobiliare
        tramite_agenzia: form.tramite_agenzia,
        nome_agenzia: form.tramite_agenzia ? (form.nome_agenzia || null) : null
      };
      
      if (editingId) {
        await api.put(`${getApiPrefix()}/appartamenti/${editingId}`, payload);
        showSuccess(t("success.apartmentUpdated"));
      } else {
        const res = await api.post(`${getApiPrefix()}/appartamenti`, payload);
        const newId = res.data?.id;
        // Crea il primo periodo contrattuale se ci sono dati
        if (newId && periodoForm.data_inizio && periodoForm.canone_mensile) {
          try {
            await api.post(`${getApiPrefix()}/appartamenti/${newId}/periodi`, periodoForm);
          } catch (e) { /* ignora errore periodo, l'appartamento è già salvato */ }
        }
        showSuccess(t("success.apartmentCreated"));
      }
      setDialogOpen(false);
      clearFormErrors();
      fetchAppartamenti();
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    } finally {
      setSavingAppartamento(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("confirm.deleteApartment"))) return;
    setDeletingAppartamento(id);
    try {
      await api.delete(`${getApiPrefix()}/appartamenti/${id}`);
      showSuccess(t("success.apartmentDeleted"));
      fetchAppartamenti();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    } finally {
      setDeletingAppartamento(null);
    }
  };

  const handleBulkDelete = async (ids) => {
    let deleted = 0;
    for (const id of ids) {
      try {
        await api.delete(`${getApiPrefix()}/appartamenti/${id}`);
        deleted++;
      } catch (err) { }
    }
    showSuccess(`${deleted} appartamenti eliminati`);
    fetchAppartamenti();
  };

  // ==================== PERIODI CONTRATTUALI ====================
  const fetchPeriodi = async (appId) => {
    setLoadingPeriodi(true);
    try {
      const res = await api.get(`${getApiPrefix()}/appartamenti/${appId}/periodi`);
      setPeriodi(res.data || []);
    } catch { setPeriodi([]); }
    finally { setLoadingPeriodi(false); }
  };

  const handleAddPeriodo = async () => {
    if (!editingId || !periodoForm.data_inizio || !periodoForm.canone_mensile) return;
    try {
      await api.post(`${getApiPrefix()}/appartamenti/${editingId}/periodi`, periodoForm);
      showSuccess("Periodo aggiunto");
      setPeriodoForm({ data_inizio: "", data_fine: "", canone_mensile: "", importo_spese_condominiali: "", note: "" });
      await fetchPeriodi(editingId);
      fetchAppartamenti();
    } catch (err) { handleApiError(err, "Errore aggiunta periodo"); }
  };

  const handleUpdatePeriodo = async (periodoId) => {
    if (!editingId) return;
    try {
      await api.put(`${getApiPrefix()}/appartamenti/${editingId}/periodi/${periodoId}`, periodoForm);
      showSuccess("Periodo aggiornato");
      setEditingPeriodoId(null);
      setPeriodoForm({ data_inizio: "", data_fine: "", canone_mensile: "", importo_spese_condominiali: "", note: "" });
      await fetchPeriodi(editingId);
      fetchAppartamenti();
    } catch (err) { handleApiError(err, "Errore aggiornamento periodo"); }
  };

  const handleDeletePeriodo = async (periodoId) => {
    if (!editingId || !confirm("Eliminare questo periodo?")) return;
    try {
      await api.delete(`${getApiPrefix()}/appartamenti/${editingId}/periodi/${periodoId}`);
      showSuccess("Periodo eliminato");
      await fetchPeriodi(editingId);
      fetchAppartamenti();
    } catch (err) { handleApiError(err, "Errore eliminazione periodo"); }
  };


  // Stats  
  const countAttivi = appartamenti.filter(a => a.stato === 'attivo').length;
  const countNonAttivi = appartamenti.filter(a => a.stato !== 'attivo').length;
  const attivi = appartamenti.filter(a => a.stato === 'attivo');
  const totaleCanoni = attivi.reduce((sum, a) => sum + (parseFloat(a.canone_mensile) || 0), 0);
  const totaleSpeseCond = attivi.reduce((sum, a) => sum + (parseFloat(a.importo_spese_condominiali) || 0), 0);
  const totaleCostoMensile = totaleCanoni + totaleSpeseCond;
  
  // Dati filtrati per stato
  const filteredData = filtroStato === 'tutti' ? appartamenti : appartamenti.filter(a => a.stato === filtroStato);

  // Mappa frequenza per visualizzazione
  const frequenzaLabel = (freq) => {
    const map = {
      mensile: "Mensile",
      bimestrale: "Bimestrale",
      trimestrale: "Trimestrale",
      semestrale: "Semestrale"
    };
    return map[freq] || freq || "Mensile";
  };

  // Colonne DataTable
  const formatDate = (v) => {
    if (!v) return null;
    const s = String(v).split('T')[0];
    const parts = s.includes('-') ? s.split('-') : s.split('/');
    if (parts.length === 3) {
      if (parts[0].length === 4) return `${parts[2].padStart(2,'0')}/${parts[1].padStart(2,'0')}/${parts[0]}`;
      return `${parts[0].padStart(2,'0')}/${parts[1].padStart(2,'0')}/${parts[2]}`;
    }
    return s;
  };

  const columns = [
    { key: 'indirizzo', label: 'Indirizzo' },
    { key: 'citta', label: 'Città', width: '90px' },
    { key: 'proprietario', label: 'Proprietario', width: '130px' },
    { key: 'canone_mensile', label: 'Canone', align: 'right', format: 'currency', width: '95px' },
    { 
      key: 'frequenza_pagamento', 
      label: 'Frequenza', 
      width: '90px',
      render: (value) => frequenzaLabel(value)
    },
    { key: 'data_inizio_contratto', label: 'Inizio', width: '95px',
      render: (value) => formatDate(value) || <span className="text-muted-foreground text-xs">-</span>
    },
    { key: 'data_fine_contratto', label: 'Fine', width: '95px',
      render: (value) => value ? formatDate(value) : <span className="text-muted-foreground text-xs">In corso</span>
    },
    { key: 'giorno_pagamento', label: 'Giorno', align: 'center', width: '60px' },
    { key: 'posti_letto', label: 'Letti', align: 'center', width: '50px' },
    { key: 'nome_agenzia', label: 'Agenzia', width: '110px',
      render: (value) => value || <span className="text-muted-foreground text-xs">-</span>
    },
    { 
      key: 'stato', 
      label: 'Stato',
      width: '90px',
      render: (value) => (
        <Badge variant={value === 'attivo' ? 'default' : 'secondary'} className={value === 'attivo' ? 'bg-green-100 text-green-700 hover:bg-green-100' : 'bg-red-50 text-red-500'}>
          {value === 'attivo' ? 'Attivo' : 'Non attivo'}
        </Badge>
      )
    },
    { 
      key: 'actions', 
      label: t('common.actions'), 
      align: 'right',
      width: '80px',
      sortable: false, 
      filterable: false,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => openEditDialog(row)} title={t("common.edit")}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(row.id)} title={t("common.delete")}>
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      )
    }
  ];

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="appartamenti-page">
      <div className="flex items-center gap-3">
        <Home className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("apartments.title")}</h1>
          <p className="text-muted-foreground text-sm">Gestione immobili in affitto</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Home className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">{t('apartments.title')}</p>
                <p className="text-2xl font-bold tracking-tight">{appartamenti.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                <span className="text-green-600 font-bold">{countAttivi}</span>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase">Contratti Attivi</p>
                <p className="text-2xl font-bold tracking-tight">{countAttivi}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Euro className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Canoni Mensili</p>
                <p className="text-2xl font-bold tracking-tight">{formatCurrency(totaleCanoni)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Euro className="h-8 w-8 text-amber-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">{t('common.condominiumFees')}</p>
                <p className="text-2xl font-bold tracking-tight">{formatCurrency(totaleSpeseCond)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Euro className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">{t('common.totalMonthlyCost')}</p>
                <p className="text-2xl font-bold tracking-tight">{formatCurrency(totaleCostoMensile)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <DataTable
        columns={columns}
        data={filteredData}
        onDelete={canEditAppartamenti ? handleBulkDelete : undefined}
        idField="id"
        emptyMessage="Nessun appartamento trovato"
        toolbar={
          <div className="flex items-center gap-2">
            <div className="flex bg-muted rounded-lg p-0.5">
              <Button variant={filtroStato === 'attivo' ? 'default' : 'ghost'} size="sm" className="h-7 text-xs rounded-md" onClick={() => setFiltroStato('attivo')}>
                Attivi ({countAttivi})
              </Button>
              <Button variant={filtroStato === 'non_attivo' ? 'default' : 'ghost'} size="sm" className="h-7 text-xs rounded-md" onClick={() => setFiltroStato('non_attivo')}>
                Non attivi ({countNonAttivi})
              </Button>
              <Button variant={filtroStato === 'tutti' ? 'default' : 'ghost'} size="sm" className="h-7 text-xs rounded-md" onClick={() => setFiltroStato('tutti')}>
                Tutti ({appartamenti.length})
              </Button>
            </div>
            {canEditAppartamenti && (
              <Button onClick={openAddDialog} data-testid="add-appartamento-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nuovo Appartamento
              </Button>
            )}
          </div>
        }
      />

      {/* Dialog Crea/Modifica */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingId(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? t("apartments.editApartment") : t("apartments.newApartment")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Indirizzo *</Label>
                <InputWithError 
                  value={form.indirizzo} 
                  onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} 
                  onValidate={() => validateField('indirizzo', form.indirizzo)}
                  error={formErrors.indirizzo}
                  placeholder="Via Roma 1" 
                />
              </div>
              <div>
                <Label>Città *</Label>
                <InputWithError 
                  value={form.citta} 
                  onChange={(e) => setForm({ ...form, citta: e.target.value })} 
                  onValidate={() => validateField('citta', form.citta)}
                  error={formErrors.citta}
                  placeholder="Milano" 
                />
              </div>
              <div>
                <Label>Area</Label>
                <Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} placeholder="Es: Nord Italia, Lombardia..." />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Posti Letto</Label>
                <Input type="number" min="1" value={form.posti_letto} onChange={(e) => setForm({ ...form, posti_letto: e.target.value })} placeholder="1" />
              </div>
              <div>
                <Label>Conto Canone (per imputazione costo)</Label>
                <SearchableSelect
                  options={[
                    { value: "", label: "-- Nessuno --" },
                    ...accounts.map(a => ({ value: a.id, label: `${a.codice} - ${a.nome}` }))
                  ]}
                  value={form.conto_canone_id || ""}
                  onValueChange={(v) => setForm({ ...form, conto_canone_id: v })}
                  placeholder={t("common.selectPlaceholder")}
                  searchPlaceholder={t("common.searchPlaceholder")}
                  emptyMessage="Nessun conto trovato"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('common.owner')}</Label>
                <Input value={form.proprietario} onChange={(e) => setForm({ ...form, proprietario: e.target.value })} placeholder="Nome proprietario" />
              </div>
              <div>
                <Label>IBAN Proprietario</Label>
                <Input value={form.iban_proprietario} onChange={(e) => setForm({ ...form, iban_proprietario: e.target.value })} placeholder="IT60X0542811101000000123456" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Frequenza Pagamento</Label>
                <Select value={form.frequenza_pagamento} onValueChange={(v) => setForm({ ...form, frequenza_pagamento: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mensile">{t("common.monthly")}</SelectItem>
                    <SelectItem value="bimestrale">{t('common.bimonthly')}</SelectItem>
                    <SelectItem value="trimestrale">{t("common.quarterly")}</SelectItem>
                    <SelectItem value="semestrale">{t("common.semiannual")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Giorno Pagamento</Label>
                <Select value={form.giorno_pagamento} onValueChange={(v) => setForm({ ...form, giorno_pagamento: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(28)].map((_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* =============== CONTRATTO =============== */}
            <div className="border rounded-lg p-4 space-y-3 border-emerald-300 bg-emerald-50/30">
              <div className="flex items-center justify-between">
                <Label className="font-medium flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Contratto
                </Label>
                {periodi.length > 0 && (
                  <Badge variant="outline" className="text-xs">
                    {periodi[0].data_fine ? `${periodi[0].data_inizio} - ${periodi[0].data_fine}` : `Dal ${periodi[0].data_inizio}`}
                    {' | '}{formatCurrency(periodi[0].canone_mensile)}/mese
                  </Badge>
                )}
              </div>

              {/* Contratto attuale (primo periodo = più recente) */}
              {periodi.length > 0 && !editingPeriodoId && (
                <div className="p-3 bg-white border rounded-md space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Contratto attuale</span>
                    <div className="flex gap-1.5">
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => {
                        const p = periodi[0];
                        setEditingPeriodoId(p.id);
                        setPeriodoForm({ data_inizio: p.data_inizio || "", data_fine: p.data_fine || "", canone_mensile: String(p.canone_mensile || ""), importo_spese_condominiali: String(p.importo_spese_condominiali || ""), note: p.note || "" });
                      }}>
                        <Pencil className="h-3 w-3 mr-1" />{t('common.edit')}</Button>
                      <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => {
                        // Rinnova: chiudi periodo corrente e prepara nuovo
                        const oggi = new Date().toISOString().split('T')[0];
                        const p = periodi[0];
                        setPeriodoForm({ data_inizio: oggi, data_fine: "", canone_mensile: String(p.canone_mensile || ""), importo_spese_condominiali: String(p.importo_spese_condominiali || ""), note: "Rinnovo" });
                        setEditingPeriodoId('new_rinnovo');
                      }}>
                        <RefreshCw className="h-3 w-3 mr-1" /> Rinnova
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-3 text-sm">
                    <div><span className="text-muted-foreground text-xs block">{t('common.start')}</span><span className="font-mono">{periodi[0].data_inizio}</span></div>
                    <div><span className="text-muted-foreground text-xs block">{t('common.end')}</span><span className="font-mono">{periodi[0].data_fine || 'In corso'}</span></div>
                    <div><span className="text-muted-foreground text-xs block">{t('apartments.canone')}</span><span className="font-medium">{formatCurrency(periodi[0].canone_mensile)}</span></div>
                    <div><span className="text-muted-foreground text-xs block">Spese Cond.</span><span>{formatCurrency(periodi[0].importo_spese_condominiali)}</span></div>
                  </div>
                  {periodi[0].note && <p className="text-xs text-muted-foreground italic">{periodi[0].note}</p>}
                </div>
              )}

              {/* Form modifica/rinnovo/nuovo */}
              {(editingPeriodoId || periodi.length === 0) && (
                <div className="p-3 bg-white border rounded-md border-dashed space-y-2">
                  <span className="text-sm font-medium">
                    {editingPeriodoId === 'new_rinnovo' ? 'Rinnovo contratto' : editingPeriodoId ? 'Modifica periodo' : 'Primo contratto'}
                  </span>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Data Inizio *</Label>
                      <DateInput value={periodoForm.data_inizio} onChange={(e) => setPeriodoForm({...periodoForm, data_inizio: e.target.value})} className="h-9" />
                    </div>
                    <div>
                      <Label className="text-xs">{t('common.endDate')}</Label>
                      <DateInput value={periodoForm.data_fine} onChange={(e) => setPeriodoForm({...periodoForm, data_fine: e.target.value})} className="h-9" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Canone Mensile (€) *</Label>
                      <Input type="number" step="0.01" value={periodoForm.canone_mensile} onChange={(e) => setPeriodoForm({...periodoForm, canone_mensile: e.target.value})} placeholder="800.00" className="h-9" />
                    </div>
                    <div>
                      <Label className="text-xs">Spese Condominiali (€/mese)</Label>
                      <Input type="number" step="0.01" value={periodoForm.importo_spese_condominiali} onChange={(e) => setPeriodoForm({...periodoForm, importo_spese_condominiali: e.target.value})} placeholder="150.00" className="h-9" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">{t('common.notes')}</Label>
                    <Input value={periodoForm.note} onChange={(e) => setPeriodoForm({...periodoForm, note: e.target.value})} placeholder="Es: Rinnovo annuale" className="h-9" />
                  </div>
                  <div className="flex gap-2 pt-1">
                    {editingPeriodoId === 'new_rinnovo' ? (
                      <Button size="sm" onClick={async () => {
                        // Chiudi vecchio periodo (ieri) e crea nuovo
                        const ieri = new Date(Date.now() - 86400000).toISOString().split('T')[0];
                        const oldP = periodi[0];
                        await api.put(`${getApiPrefix()}/appartamenti/${editingId}/periodi/${oldP.id}`, { ...oldP, data_fine: ieri });
                        await handleAddPeriodo();
                        setEditingPeriodoId(null);
                      }} disabled={!periodoForm.data_inizio || !periodoForm.canone_mensile}>
                        Conferma Rinnovo
                      </Button>
                    ) : editingPeriodoId ? (
                      <Button size="sm" onClick={() => handleUpdatePeriodo(editingPeriodoId)}>{t('common.saveChanges')}</Button>
                    ) : (
                      <Button size="sm" onClick={async () => {
                        if (editingId) {
                          await handleAddPeriodo();
                        } else {
                          // Per nuovo appartamento, salva nei campi principali
                          setForm({ ...form, canone_mensile: periodoForm.canone_mensile, importo_spese_condominiali: periodoForm.importo_spese_condominiali, data_inizio_contratto: periodoForm.data_inizio, data_fine_contratto: periodoForm.data_fine });
                          showSuccess("Contratto impostato. Salva l'appartamento per confermare.");
                        }
                      }} disabled={!periodoForm.data_inizio || !periodoForm.canone_mensile}>
                        {editingId ? 'Aggiungi Periodo' : 'Imposta Contratto'}
                      </Button>
                    )}
                    {editingPeriodoId && (
                      <Button variant="outline" size="sm" onClick={() => { setEditingPeriodoId(null); setPeriodoForm({ data_inizio: "", data_fine: "", canone_mensile: "", importo_spese_condominiali: "", note: "" }); }}>{t('common.cancel')}</Button>
                    )}
                  </div>
                </div>
              )}

              {/* Storico periodi precedenti */}
              {periodi.length > 1 && !editingPeriodoId && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-muted-foreground text-xs hover:text-foreground">
                    Storico ({periodi.length - 1} {periodi.length - 1 === 1 ? 'periodo precedente' : 'periodi precedenti'})
                  </summary>
                  <div className="space-y-1 mt-2">
                    {periodi.slice(1).map((p) => (
                      <div key={p.id} className="flex items-center justify-between p-2 bg-muted/30 border rounded text-xs">
                        <div className="flex items-center gap-2 flex-1">
                          <span className="font-mono">{p.data_inizio}</span>
                          <span className="text-muted-foreground">-</span>
                          <span className="font-mono">{p.data_fine || '?'}</span>
                          <Badge variant="outline" className="text-[10px]">{formatCurrency(p.canone_mensile)}</Badge>
                          {p.importo_spese_condominiali > 0 && <Badge variant="secondary" className="text-[10px]">Sp: {formatCurrency(p.importo_spese_condominiali)}</Badge>}
                          {p.note && <span className="text-muted-foreground italic">{p.note}</span>}
                        </div>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={() => handleDeletePeriodo(p.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>

            {/* Sezione Spese Condominiali */}
            <div className={`border rounded-lg p-4 space-y-4 ${form.ha_spese_condominiali ? 'border-blue-300 bg-blue-50/50' : 'border-dashed'}`}>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="ha_spese_condominiali"
                  checked={form.ha_spese_condominiali}
                  onCheckedChange={(checked) => setForm({ ...form, ha_spese_condominiali: checked })}
                />
                <Label htmlFor="ha_spese_condominiali" className="cursor-pointer font-medium">
                  Spese Condominiali Presenti
                </Label>
              </div>

              {form.ha_spese_condominiali && (
                <>
                  <div>
                    <Label>Conto Spese Condominiali (per imputazione costo)</Label>
                    <SearchableSelect
                      options={[
                        { value: "", label: "-- Nessuno --" },
                        ...accounts.map(a => ({ value: a.id, label: `${a.codice} - ${a.nome}` }))
                      ]}
                      value={form.conto_spese_condominiali_id || ""}
                      onValueChange={(v) => setForm({ ...form, conto_spese_condominiali_id: v })}
                      placeholder={t("common.selectPlaceholder")}
                      searchPlaceholder={t("common.searchPlaceholder")}
                      emptyMessage="Nessun conto trovato"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Le spese condominiali coprono:</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="spese_generali"
                          checked={form.spese_cond_spese_generali}
                          onCheckedChange={(checked) => setForm({ ...form, spese_cond_spese_generali: checked })}
                        />
                        <Label htmlFor="spese_generali" className="cursor-pointer text-sm">Spese Generali</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="spese_luce"
                          checked={form.spese_cond_luce}
                          onCheckedChange={(checked) => setForm({ ...form, spese_cond_luce: checked })}
                        />
                        <Label htmlFor="spese_luce" className="cursor-pointer text-sm">Luce</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="spese_gas"
                          checked={form.spese_cond_gas}
                          onCheckedChange={(checked) => setForm({ ...form, spese_cond_gas: checked })}
                        />
                        <Label htmlFor="spese_gas" className="cursor-pointer text-sm">Gas</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="spese_acqua"
                          checked={form.spese_cond_acqua}
                          onCheckedChange={(checked) => setForm({ ...form, spese_cond_acqua: checked })}
                        />
                        <Label htmlFor="spese_acqua" className="cursor-pointer text-sm">Acqua</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="spese_tari"
                          checked={form.spese_cond_tari}
                          onCheckedChange={(checked) => setForm({ ...form, spese_cond_tari: checked })}
                        />
                        <Label htmlFor="spese_tari" className="cursor-pointer text-sm">TARI</Label>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Sezione Agenzia Immobiliare */}
            <div className={`border rounded-lg p-4 space-y-4 ${form.tramite_agenzia ? 'border-purple-300 bg-purple-50/50' : 'border-dashed'}`}>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="tramite_agenzia"
                  checked={form.tramite_agenzia}
                  onCheckedChange={(checked) => setForm({ ...form, tramite_agenzia: checked })}
                />
                <Label htmlFor="tramite_agenzia" className="cursor-pointer font-medium">
                  Tramite Agenzia Immobiliare
                </Label>
              </div>

              {form.tramite_agenzia && (
                <div>
                  <Label>Nome Agenzia *</Label>
                  <Input
                    value={form.nome_agenzia}
                    onChange={(e) => setForm({ ...form, nome_agenzia: e.target.value })}
                    placeholder="Es: Tecnocasa, Gabetti..."
                  />
                </div>
              )}
            </div>

            <div>
              <Label>{t("common.notes")}</Label>
              <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={t("common.notesOptional")} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={savingAppartamento}>{t("common.cancel")}</Button>
            <Button onClick={handleSubmit} disabled={savingAppartamento}>
              {savingAppartamento ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("common.saving")}</> : (editingId ? t("common.save") : t("common.create"))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
